import * as React from "react"
import { observable, action, computed } from "mobx"
import { MembersMapI, BookingFlow } from "../../BookingFlow"
import { Modal } from "../../utils/Modal"
import { SignWaiver } from "./SignWaiver"
import { BookingDB } from "../../db/BookingLightDB"
import { Member } from "BookingSystem/db/Member"
import { Waiver } from "BookingSystem/db/Waiver"

export class WaiverEditor implements MembersMapI {
  constructor(db: BookingDB, flow: BookingFlow, adult: Member, waiver: Waiver) {
    this.flow = flow
    this.db = db
    this.adult = adult
    this.waiver = waiver
  }

  @observable
  flow: BookingFlow

  @observable
  db: BookingDB

  @observable
  adult: Member

  @observable
  waiver: Waiver

  @observable
  isSelectable = true

  @observable
  isEditable = false

  trimmedDataUrl = null
  sigPad: any = {}
  clear = () => {
    this.sigPad.clear()
  }

  @computed
  get minorsOnWaiver(): Member[] {
    return this.waiver.members
      ? Object.values(this.waiver.members).filter(m => m.isMinor)
      : []
  }

  @computed
  get isSigned() {
    return this.waiver.isSigned
  }

  @computed
  get memberList() {
    return this.flow.currentBooking.minorsNeedingWaiver
  }

  has(member: Member) {
    return this.waiver.members.filter(m => m.id === member.id).length > 0
  }

  @computed
  get isMemberOwner() {
    return (
      this.flow.currentBooking.memberOwner &&
      this.flow.currentBooking.memberOwner.fullName === this.name
    )
  }

  onEdit(member: Member) {}

  onDelete(member: Member) {}

  invalidateMinor = new Date()

  @action.bound
  toggleMembership(member: Member) {
    if (this.has(member)) {
      delete this.waiver.object.$members[member.id]
    } else {
      this.waiver.object.$members[member.id] = member.id
    }
  }

  @computed
  get numOfMembers() {
    return this.flow.currentBooking.membersNeedingWaiver.length
  }

  @computed
  get minorsNeedingWaiver() {
    return this.flow.currentBooking.membersNeedingWaiver.filter(m => m.isMinor)
  }

  @computed
  get numOfMembersOnWaiver() {
    return this.waiver.members.length
  }

  @computed
  get numOfMinors() {
    return this.flow.currentBooking.minorsNeedingWaiver.length
  }

  @computed
  get name() {
    return this.adult.fullName
  }

  @computed
  get readyToSign() {
    if (this.minorsNeedingWaiver.length > 0)
      return this.numOfMembersOnWaiver > 0 && !this.waiver.isSigned
    return !this.waiver.isSigned
  }

  @computed
  get signModal() {
    const m = new Modal(this.flow, this.signWaiver)
    m.primaryButtonText = "Submit"
    m.primaryDisabled = false
    m.title = "Please Scroll Down & Sign"
    m.children = React.createElement(SignWaiver, { model: this })
    return m
  }

  @action.bound
  componentDidMount() {
    this.flow.currentBooking.minorsNeedingWaiver
      .filter(minor => this.isMemberOwner)
      .forEach(minor => this.toggleMembership(minor))
  }

  @action.bound
  componentWillUnMount() {}

  @action.bound
  async signWaiver() {
    if (this.sigPad.isEmpty()) {
      alert("Please sign the waiver before proceeding")
      return
    }

    try {
      const signatureURL = this.sigPad.getTrimmedCanvas().toDataURL("image/png")
      this.waiver.sign(signatureURL)
      this.db.save(this.waiver)
      this.db.save(this.adult)
      await this.db.postNextSyncRequest()

      if (this.flow.currentBooking.membersNeedingWaiver.length <= 0)
        this.flow.advance()
    } catch (err) {
      console.error("Error signing waiver")
      console.error(err)
    }
  }

  @action.bound
  showSignModal() {
    this.flow.showModal(() => this.signModal)
  }
}
